'use strict';
import { useEffect, useMemo, useRef } from 'react';

export const useAccessQueries = ({
	accessTabsConfig,
	activeTabIndex,
	searchText,
	pageSize,
	useUsersQueryHook,
	useManagedProjectsQueryHook,
	useRolesQueryHook,
	useSearchUsersQueryHook,
	useSearchProjectsQueryHook,
	useSearchRolesQueryHook,
}) => {
	const lastGoodSearchItemsRef = useRef({
		users: new Map(),
		projects: new Map(),
		roles: new Map(),
	});

	const currentTabConfig = accessTabsConfig[activeTabIndex];
	const shouldSearchServer = currentTabConfig?.search?.mode === 'server' &&
		searchText.length >= (currentTabConfig.search.minChars || 0);
	const searchQuery = shouldSearchServer ? searchText.toUpperCase() : '';

	const usersQueryResult = useUsersQueryHook({ skip: 0, take: pageSize });
	const projectsQueryResult = useManagedProjectsQueryHook({ skip: 0, take: pageSize });
	const rolesQueryResult = useRolesQueryHook({ skip: 0, take: pageSize });
	const searchUsersQueryResult = useSearchUsersQueryHook(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearchServer || currentTabConfig?.key !== 'users' }
	);
	const searchProjectsQueryResult = useSearchProjectsQueryHook(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearchServer || currentTabConfig?.key !== 'projects' }
	);
	const searchRolesQueryResult = useSearchRolesQueryHook(
		{ searchText: searchQuery, skip: 0, take: pageSize },
		{ skip: !shouldSearchServer || currentTabConfig?.key !== 'roles' }
	);

	const { allItems, searchItems, refetchAll, refetchSearch, isSearching, isLoadingAll, isError, error } = useMemo(() => {
		if (!currentTabConfig) {
			return {
				allItems: [],
				searchItems: [],
				refetchAll: () => {},
				refetchSearch: () => {},
				isSearching: false,
				isLoadingAll: false,
				isError: false,
				error: null,
			};
		}

		const tabKey = currentTabConfig.key;
		const currentSearchQuery = shouldSearchServer ? searchText.toUpperCase() : '';

		const getValidSearchItems = (queryResult, key) => {
			if (!shouldSearchServer) {
				return queryResult.data || [];
			}

			const currentData = queryResult.currentData;
			if (Array.isArray(currentData)) {
				return currentData;
			}

			return lastGoodSearchItemsRef.current[key].get(currentSearchQuery) || [];
		};

		if (tabKey === 'users') {
			const validSearchItems = getValidSearchItems(searchUsersQueryResult, 'users');
			return {
				allItems: usersQueryResult.data || [],
				searchItems: validSearchItems,
				refetchAll: usersQueryResult.refetch,
				refetchSearch: searchUsersQueryResult.refetch,
				isSearching: searchUsersQueryResult.isLoading || searchUsersQueryResult.isFetching || false,
				isLoadingAll: usersQueryResult.isLoading || usersQueryResult.isFetching || false,
				isError: usersQueryResult.isError || searchUsersQueryResult.isError || false,
				error: usersQueryResult.error || searchUsersQueryResult.error || null,
			};
		}
		if (tabKey === 'projects') {
			const validSearchItems = getValidSearchItems(searchProjectsQueryResult, 'projects');
			return {
				allItems: projectsQueryResult.data || [],
				searchItems: validSearchItems,
				refetchAll: projectsQueryResult.refetch,
				refetchSearch: searchProjectsQueryResult.refetch,
				isSearching: searchProjectsQueryResult.isLoading || searchProjectsQueryResult.isFetching || false,
				isLoadingAll: projectsQueryResult.isLoading || projectsQueryResult.isFetching || false,
				isError: projectsQueryResult.isError || searchProjectsQueryResult.isError || false,
				error: projectsQueryResult.error || searchProjectsQueryResult.error || null,
			};
		}
		if (tabKey === 'roles') {
			const validSearchItems = getValidSearchItems(searchRolesQueryResult, 'roles');
			return {
				allItems: rolesQueryResult.data || [],
				searchItems: validSearchItems,
				refetchAll: rolesQueryResult.refetch,
				refetchSearch: searchRolesQueryResult.refetch,
				isSearching: searchRolesQueryResult.isLoading || searchRolesQueryResult.isFetching || false,
				isLoadingAll: rolesQueryResult.isLoading || rolesQueryResult.isFetching || false,
				isError: rolesQueryResult.isError || searchRolesQueryResult.isError || false,
				error: rolesQueryResult.error || searchRolesQueryResult.error || null,
			};
		}

		return {
			allItems: [],
			searchItems: [],
			refetchAll: () => {},
			refetchSearch: () => {},
			isSearching: false,
			isLoadingAll: false,
			isError: false,
			error: null,
		};
	}, [
		currentTabConfig,
		usersQueryResult,
		projectsQueryResult,
		rolesQueryResult,
		searchUsersQueryResult,
		searchProjectsQueryResult,
		searchRolesQueryResult,
		shouldSearchServer,
		searchText,
	]);

	useEffect(() => {
		if (!shouldSearchServer) return;
		const currentSearchQuery = searchText.toUpperCase();

		const updateLastGood = (queryResult, key) => {
			const isCurrentQuery = queryResult.originalArgs?.searchText === currentSearchQuery;
			const currentData = queryResult.currentData ?? queryResult.data;
			if (isCurrentQuery && !queryResult.isFetching && !queryResult.isLoading) {
				lastGoodSearchItemsRef.current[key].set(
					currentSearchQuery,
					Array.isArray(currentData) ? currentData : []
				);
			}
		};

		updateLastGood(searchUsersQueryResult, 'users');
		updateLastGood(searchProjectsQueryResult, 'projects');
		updateLastGood(searchRolesQueryResult, 'roles');
	}, [
		shouldSearchServer,
		searchText,
		searchUsersQueryResult.data,
		searchUsersQueryResult.isFetching,
		searchUsersQueryResult.isLoading,
		searchUsersQueryResult.originalArgs,
		searchProjectsQueryResult.data,
		searchProjectsQueryResult.isFetching,
		searchProjectsQueryResult.isLoading,
		searchProjectsQueryResult.originalArgs,
		searchRolesQueryResult.data,
		searchRolesQueryResult.isFetching,
		searchRolesQueryResult.isLoading,
		searchRolesQueryResult.originalArgs,
	]);

	return {
		currentTabConfig,
		shouldSearchServer,
		searchQuery,
		allItems,
		searchItems,
		refetchAll,
		refetchSearch,
		isSearching,
		isLoadingAll,
		isError,
		error,
		usersQueryResult,
		projectsQueryResult,
		rolesQueryResult,
		searchUsersQueryResult,
		searchProjectsQueryResult,
		searchRolesQueryResult,
	};
};

export default useAccessQueries;
